import { useMemo } from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { useTestSetsList } from "@/test-set/catalog"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"

import { useRunsFilter } from "../../contexts/RunsFilterContext"
import type { FilterSectionProps } from "../../types/filters"

const TestSetIcon = ENTITY_ICONS["test-sets"]

export const TestSetFilterSection: React.FC<FilterSectionProps> = ({
  isOpen,
  onToggle,
  filterEnabled,
}) => {
  const { testSetFilters, setTestSetFilters } = useRunsFilter()
  const {
    data: testSets,
    isError,
    isLoading,
  } = useTestSetsList({
    enabled: filterEnabled,
  })

  const items = useMemo(() => {
    if (!testSets) {
      return []
    }
    return testSets.map((testSet) => ({
      id: testSet.id,
      label: testSet.display_name || testSet.id,
      searchValue: testSet.display_name || testSet.id,
    }))
  }, [testSets])

  const isDisabled = isFilterSectionDisabled({
    activeFilterCount: testSetFilters.length,
    isDeferred: filterEnabled === false,
    isError,
    isLoading,
    optionCount: items.length,
  })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={isDisabled}
      emptyMessage="No test sets available in this workspace."
      trigger={
        <FilterTrigger
          label="Test Sets"
          icon={<TestSetIcon className="w-4 h-4" />}
          isActive={testSetFilters.length > 0}
          disabled={isDisabled}
        />
      }
    >
      <SearchableCheckboxList
        items={items}
        selectedIds={testSetFilters}
        onSelectionChange={setTestSetFilters}
        isLoading={isLoading}
        isDeferred={filterEnabled === false}
        isOpen={isOpen}
        placeholder="Search test sets..."
        emptyMessage="No test sets found"
        noItemsMessage="No test sets available"
      />
    </FilterPopover>
  )
}
